/**
 * @typedef {object} SubscriptionEvent An event emitted by a SubscriptionManager instance.
 * @property {string} type The type of event.
 * @property {*} payload The contents of the event.
 */

/**
 * @typedef {object} SubscriptionResult An object containing functions usable by a subscriber.
 * @property {Function} unsubscribe A function used to cancel the subscription.
 * @property {function(boolean): void} setSuspended A function used to suspend and unsuspend the subscription.
 */

/**
 * @typedef {object} SubscriptionOptions An object containing optional configuration for subscription.
 * @property {string} [name] The name of the subscription.
 * @property {boolean} [startSuspended] If `true`, this subscription will initially be suspended.
 * @property {boolean} [once] If `true`, remove this subscription the first time it receives an event.
 */

/** Class representing callback subscriptions that can receive published events. */
class SubscriptionManager {
  /**
   * Creates a new subscription manager.
   * @param {SubscriptionOptions} [defaults] Options that will be used as defaults for new subscriptions.
   */
  constructor(defaults) {
    if (defaults !== undefined && !(defaults instanceof Object)) {
      throw new TypeError('Invalid argument: defaults must be an object.');
    }
    /** @type {SubscriptionOptions} @private */
    this.defaults = defaults;
    /** @type {Set<Subscription>} @private */
    this.subscriptions = new Set();
  }

  /**
   * Adds a new subscription callback, to be invoked in the future when an event is published.
   * @param {string|null} type The type of event to subscribe to, or null to subscribe to all events.
   * @param {function(SubscriptionEvent): void} callback The function to be invoked when events occur.
   * @param {SubscriptionOptions} [options] Extra options to be added to the subscription.
   * @return {SubscriptionResult} An object with functions to alter the subscription.
   */
  addSubscription(type, callback, options) {
    // Apply any defaults not overwritten in options to the new subscription.
    const sub = new Subscription(type, callback, Object.assign({...this.defaults}, options));
    this.subscriptions.add(sub);
    return {
      unsubscribe: () => this.subscriptions.delete(sub),
      setSuspended: (newSuspended) => sub.suspended = newSuspended,
    };
  }

  /**
   * Determines whether there are any subscriptions.
   * @param {string} [type] If supplied, check specifically for subscriptions of this type.
   * @return {boolean} `true` if at least one subscription exists; `false` otherwise.
   */
  hasSubscriptions(type) {
    if (type) {
      // Find at least one subscription of the type and return true, else return false.
      for (const sub of this.subscriptions.values()) {
        if (sub.includes(type)) return true;
      }
      return false;
    }
    return this.subscriptions.size > 0;
  }

  /**
   * Creates and sends events to the relevant subscribers.
   * @param {string} type The type of this event.
   * @param {*} [payload] The event contents.
   */
  publish(type, payload) {
    const callbacks = [];
    // Enumerate callbacks first to avoid race conditions involving subscription changes.
    this.subscriptions.forEach((subscription) => {
      if (subscription.includes(type) && !subscription.suspended) {
        callbacks.push(subscription.callback);
        if (subscription.once) {
          this.subscriptions.delete(subscription);
        }
      }
    });
    callbacks.forEach((cb) => cb({type, payload}));
  }
}

/** Class representing a subscribed callback. */
class Subscription {
  /**
   * Creates a new subscription.
   * @param {string|null} type The type of event to subscribe to, or null to subscribe to all events.
   * @param {Function<SubscriptionEvent>} callback The function to be invoked when events occur.
   * @param {object} [options] An object containing extra settings.
   */
  constructor(type, callback, options) {
    /** @type {string|null} */
    this.type = type;
    /** @type {Function<SubscriptionEvent>} */
    this.callback = callback;
    /** @type {boolean} */
    this.suspended = !!options?.startSuspended || false;

    // Optional settings:
    /** @type {boolean} If true, this subscription will be removed after receiving its first event. */
    this.once = options?.once || false;
    // name: A label for the subscription.
    /** @type {string} A label for the subscription. */
    this.name = options?.name;
  }

  /**
   * Determines whether this subscription includes a particular type of event.
   * @param {string} type The type of event.
   * @return {boolean} `true` if this subscription includes events of the given type; `false` otherwise.
   */
  includes(type) {
    // If type is null, the subscription covers all event types.
    return this.type === null || this.type === type;
  }
}

export {SubscriptionManager};
